if (!customElements.get('routine-gift-offer')) {
  customElements.define(
    'routine-gift-offer',
    class RoutineGiftOffer extends HTMLElement {
      constructor() {
        super();

        this.buttons = this.querySelectorAll('[data-routine-submit]');
        this.button = this.buttons[0];
        this.error = this.querySelector('[data-routine-error]');
        this.oilSelect = this.querySelector('[data-routine-oil-select]');
        this.oilVariantData = this.getOilVariantData();
        this.oilTitleTargets = this.querySelectorAll('[data-routine-oil-title]');
        this.oilPriceTarget = this.querySelector('[data-routine-oil-price]');
        this.oilImageWrappers = this.querySelectorAll('[data-routine-oil-image-wrapper]');
        this.oilLinkTargets = this.querySelectorAll('[data-routine-oil-link]');
        this.lastOilVariantId = null;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.buttons.forEach((button) => button.addEventListener('click', this.onSubmit.bind(this)));
        this.oilSelect?.addEventListener('change', this.updateOilDetails.bind(this));
        document.addEventListener('change', this.updateOilDetails.bind(this));
        this.updateOilDetails();

        if (this.dataset.currentRole === 'oil') {
          this.variantWatcher = window.setInterval(() => this.updateOilDetails(), 400);
        }
      }

      disconnectedCallback() {
        if (this.variantWatcher) window.clearInterval(this.variantWatcher);
      }

      getOilVariantData() {
        const script = this.querySelector('[data-routine-oil-variants]');
        if (!script?.textContent) return new Map();

        try {
          return new Map(
            JSON.parse(script.textContent).map((variant) => [String(variant.id), variant])
          );
        } catch (error) {
          console.warn('Routine gift oil variant data could not be parsed:', error);
          return new Map();
        }
      }

      getCurrentVariantId() {
        const currentRole = this.dataset.currentRole;
        if (currentRole === 'oil') {
          const formInput = document.querySelector('product-form form [name="id"], form[action*="/cart/add"] [name="id"]');
          return formInput?.value;
        }

        return this.dataset.softenerVariantId;
      }

      getOilVariantId() {
        if (this.oilSelect?.value) return this.oilSelect.value;
        if (this.dataset.currentRole === 'oil') return this.getCurrentVariantId();
        return this.oilSelect?.value;
      }

      normalizeVariantTitle(title) {
        if (!title || title === 'Default Title') return '';
        return title;
      }

      updateOilDetails() {
        const oilVariantId = String(this.getOilVariantId() || '');
        if (!oilVariantId || oilVariantId === this.lastOilVariantId) return;

        const variant = this.oilVariantData.get(oilVariantId);
        if (!variant) return;

        this.lastOilVariantId = oilVariantId;
        const title = this.normalizeVariantTitle(variant.title);

        this.oilTitleTargets.forEach((target) => {
          target.textContent = title;
          target.hidden = !title;
        });

        if (this.oilPriceTarget && variant.price) {
          this.oilPriceTarget.innerHTML = variant.price;
        }

        if (variant.image) {
          this.oilImageWrappers.forEach((wrapper) => {
            const image = wrapper.querySelector('img');
            if (!image) return;
            image.removeAttribute('srcset');
            image.src = variant.image;
            image.alt = title || image.alt;
          });
        }

        if (this.dataset.oilProductUrl) {
          this.oilLinkTargets.forEach((link) => {
            link.href = `${this.dataset.oilProductUrl}?variant=${oilVariantId}`;
          });
        }
      }

      getItems() {
        const oilVariantId = this.getOilVariantId();
        const softenerVariantId = this.dataset.softenerVariantId;
        const scrubberVariantId = this.dataset.scrubberVariantId;

        if (!oilVariantId || !softenerVariantId || !scrubberVariantId) return [];

        return [
          {
            id: Number(oilVariantId),
            quantity: 1,
            properties: {
              _routine_gift_offer: 'beard-oil-softener-scrubber',
            },
          },
          {
            id: Number(softenerVariantId),
            quantity: 1,
            properties: {
              _routine_gift_offer: 'beard-oil-softener-scrubber',
            },
          },
          {
            id: Number(scrubberVariantId),
            quantity: 1,
            properties: {
              _routine_gift_offer: 'beard-oil-softener-scrubber',
              _routine_gift_item: 'true',
            },
          },
        ];
      }

      setLoading(isLoading) {
        if (!this.buttons.length) return;
        this.buttons.forEach((button) => {
          if (isLoading) {
            button.dataset.originalText = button.textContent.trim();
            button.textContent = this.dataset.loadingLabel || button.textContent;
            button.setAttribute('aria-disabled', 'true');
          } else {
            button.textContent = button.dataset.originalText || this.dataset.successLabel || button.textContent;
            button.removeAttribute('aria-disabled');
          }
        });
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message || this.dataset.errorLabel || 'Could not add the routine. Please try again.';
        this.error.hidden = false;
      }

      clearError() {
        if (!this.error) return;
        this.error.textContent = '';
        this.error.hidden = true;
      }

      async onSubmit() {
        if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;

        this.clearError();
        const items = this.getItems();
        if (!items.length) {
          this.showError();
          return;
        }

        this.setLoading(true);

        try {
          const body = { items };
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          const cartAddUrl = window.routes?.cart_add_url || '/cart/add';
          const endpoint = cartAddUrl.endsWith('.js') ? cartAddUrl : `${cartAddUrl}.js`;

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });

          const parsed = await response.json();
          if (!response.ok || parsed.status) {
            throw new Error(parsed.description || parsed.message || this.dataset.errorLabel);
          }

          if (this.cart && parsed.sections) {
            if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.cart.renderContents(parsed);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          console.error('Routine gift add failed:', error);
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
